import { ContentCard } from "@travellocal/ui";
import { Icon } from "armstrong-react";
import classNames from "classnames";
import { LocationDescriptorObject } from "history";
import React from "react";
import { Helmet } from "react-helmet";
import Measure, { ContentRect } from "react-measure";
import { connect } from "react-redux";
import { browserHistory } from "react-router";
import { push } from "react-router-redux";
import Sticky from "react-stickynode";
import _ from "underscore";
import * as zenscroll from "zenscroll";
import * as WebRoutes from "@travellocal/utils/webRoutes";

import { isBrowser } from "../../../core/detection";
import { Sort } from "../../../shared/components/dataTable";
import { FlexiCard, NewsletterSubCard, PlaceholderCard } from "../../../shared/components/inspiration/cards";
import * as InspirationResources from "../../../shared/components/inspiration/resources";
import { CollectionKeyHelper } from "../../../shared/helpers/contentCollectionHelpers";
import { ContentTypeEnumHelper, MonthEnumHelper } from "@travellocal/utils/enumHelper";
import { lockPageScroll, unlockPageScroll } from "../../../../../packages/ui/src/utils/uiHelpers";
import { RoutesInvoker } from "../../routesInvoker";
import { IconButton, Loader, SvgIcon } from "./../../../core/atoms/atoms";
import { apiPromiseHook } from "./../../../shared/flux/actions/enterHooks";
import { ContentTypeFilter, FilterAccordion, FilterPanel, FilterPanelDefinition, LocationFilter, MonthFilter, ThemeFilter } from "./../../components/content/filters";
import { ActionPromises } from "./../../state/actionPromises";
import { EnterHookPromises } from "./../../state/enterHookPromises";

import "./index.scss";

const CONTENT_PAGE_SIZE = 30;

const allContentTypes = [
  ContentType.BlogPost,
  ContentType.SampleItinerary,
  ContentType.Review,
];

enum CardSize {
  Small,
  Mid,
}

interface StateProps {
  /** Currently selected currency. Need to call api again whenever this changes */
  currency: IState<IBaseCurrency>;

  /** Content for the current view determined by the filters */
  content: IPagerState<IContent>;

  currentRoute: RouterReducer;
  filterThemes: IPagerState<ITheme>;
  filterRegions: IState<IRegionSummary[]>;
  filterCountries: IState<ICountrySummary[]>;
}

interface DispatchProps {
  loadContent: (contentFilters: IListContentParams, page: number) => Promise<any>;
  refreshContent: (contentFilters: IListContentParams, currentPage: number) => Promise<any>;
  navigate: (route: string) => void;
}

interface InspirationFilterState {
  selectedTypes?: ContentType[];
  selectedThemes?: ITheme[];
  selectedMonths?: Month[];
  selectedCountries?: ICountrySummary[];
  selectedCardSize?: CardSize;
  cardWrapDimensions?: ContentRect;

  /** True when the filters panel has been opened from the compact view. */
  filterDrawerOpen?: boolean;
}

interface OwnProps {
  location: LocationDescriptorObject;
}

type InspirationProps = StateProps & DispatchProps & OwnProps;

export class InspirationIndexComponent extends React.Component<InspirationProps, InspirationFilterState> {
  private filterAccordionRef: FilterAccordion;
  private unregisterBrowserHistory: () => void;
  private columnClasses: string;

  public constructor(props) {
    super(props);
    this.state = {
      selectedTypes: [],
      selectedThemes: [],
      selectedMonths: [],
      selectedCountries: [],
      selectedCardSize: null, // three way state, null means UI can choose a default
    };
    this.columnClasses = "column is-full-mobile is-full-tablet is-6-desktop is-4-widescreen is-4-fullhd";
  }

  private sortNumericAscending = (a: number, b: number) => a - b;

  private getCurrentFilterSet = (): IListContentParams => {
    return {
      themes: (this.state.selectedThemes || []).map(x => x.id).sort(this.sortNumericAscending),
      locations: (this.state.selectedCountries || []).map(x => x.id).sort(this.sortNumericAscending),
      months: (this.state.selectedMonths || []).sort(this.sortNumericAscending),
      statuses: [], // only admin users are able to request a diff set of statuses
      types: (this.state.selectedTypes || []).sort(this.sortNumericAscending),
    };
  }

  private loadContentPage = (index?: number) => this.props.loadContent(this.getCurrentFilterSet(), index || -1); // -1 resets pages

  private onFiltersChange = async () => {
    // scroll to top of cards to prevent pushing filters down
    if (isBrowser()) {
      zenscroll.to(document.getElementById("cards-wrap"));
    }

    await this.loadContentPage();
  }

  private resetFilters = () => {
    this.setState({
      selectedTypes: [],
      selectedThemes: [],
      selectedMonths: [],
      selectedCountries: [],
    }, () => this.onFiltersChange());
  }

  private onContentTypeToggle = (typeSelection: ContentType[]) => {
    this.setState({ selectedTypes: typeSelection || [] }, () => this.onFiltersChange());
  }

  private onThemeFilterToggle = (themeSelection: ITheme[]) => {
    this.setState({ selectedThemes: themeSelection || [] }, () => this.onFiltersChange());
  }

  private onMonthFilterToggle = (monthSelection: Month[]) => {
    this.setState({ selectedMonths: monthSelection || [] }, () => this.onFiltersChange());
  }

  private onCountryFilterToggle = (countrySelection: ICountrySummary[]) => {
    this.setState({ selectedCountries: countrySelection || [] }, () => this.onFiltersChange());
  }

  private setCardSize = (size: CardSize) => {
    this.setState({ selectedCardSize: this.state.selectedCardSize != null && size === this.state.selectedCardSize ? null : size });
  }

  private openFilterFromCompact = (panelIndex: number) => {
    lockPageScroll();
    this.filterAccordionRef.openDrawer(panelIndex);
    this.setState({ filterDrawerOpen: true });
  }
  private closeFilterFromCompact = () => {
    unlockPageScroll();
    this.setState({ filterDrawerOpen: false });
  }

  private debounceScroll = _.debounce(e => this.handleScroll(e), 300, false);
  private handleScroll = _ => {
    const contentState = this.props.content;

    if (!contentState
      || contentState.nextIndex == null
      // || this.state.error != null
      || this.state.cardWrapDimensions == null
      || contentState.status === "PENDING") {
      return;
    }

    const cardWrapEndFromTop = this.state.cardWrapDimensions.bounds.height + 100;
    const threshold = window.outerHeight * 1.2;
    if (window.pageYOffset + threshold > cardWrapEndFromTop) {
      // nextIndex might be misleading since its dependent on pageSize
      // pageSize is large if page is set on first request, but we want to increment in smaller steps
      // so need to work out the real next index in terms of the smaller increment
      const nextIndex = Math.floor((contentState.data.length / CONTENT_PAGE_SIZE) + 1);
      this.loadContentPage(nextIndex);
    }
  }

  private debounceResize = _.debounce(dim => this.handleResize(dim), 400, false);
  private handleResize = (dim: ContentRect) => this.setState({ cardWrapDimensions: dim });

  public componentDidMount() {
    const contentState = this.props.content;
    if (!contentState) {
      return;
    }

    // Params in QS will have been parsed into the store - make our view match those
    const routerFilterSet = contentState.context ? contentState.context.params as IListContentParams : null;
    if (!routerFilterSet) {
      return;
    }

    const newState: InspirationFilterState = {};

    // TODO find a JS library which does nice joins
    newState.selectedTypes = routerFilterSet.types ? allContentTypes.filter(type => routerFilterSet.types.some(t => type === t)) : [];
    newState.selectedThemes = routerFilterSet.themes ? this.props.filterThemes.data.filter(theme => routerFilterSet.themes.some(id => theme.id === id)) : [];
    newState.selectedMonths = routerFilterSet.months ? MonthEnumHelper.asArray().filter(month => routerFilterSet.months.some(id => month === id)) : [];
    newState.selectedCountries = routerFilterSet.locations ? this.props.filterCountries.data.filter(country => routerFilterSet.locations.some(id => country.id === id)) : [];

    this.setState(newState);

    window.addEventListener("scroll", this.debounceScroll);
    this.unregisterBrowserHistory = browserHistory.listenBefore(location => {
      const locationDesc = this.props.currentRoute.locationBeforeTransitions as LocationDescriptorObject;
      if (locationDesc.pathname) {
        if (location.pathname !== locationDesc.pathname) {
          // Navigation will change document height, which will trigger scroll logic, which triggers change of url, which triggers load of /inspiration after loading content detail page.
          // componentWillUnmount occurs after navigation, but scroll handler has to be removed before navigation.
          window.removeEventListener("scroll", this.debounceScroll);
        }
      }
      else {
        // TODO
        throw new Error("Not implemented: location desc as a string");
      }
    });
  }

  public componentWillUnmount() {
    if (this.unregisterBrowserHistory) { this.unregisterBrowserHistory(); }
  }

  public componentWillReceiveProps(newProps: InspirationProps) {

    // Refresh content when a link from the dropdown is clicked
    // TODO this is not a great solution. This component should be refactored to have one place where QS and state
    // are kept in sync
    if (newProps.location.query.dropdown && !_.isEqual(this.props.location.query, newProps.location.query)) {
      const { types, themes, months, locations } = newProps.location.query ?? {};
      this.setState({
        selectedTypes: types?.split(",").map(x => parseInt(x)) ?? [],
        selectedThemes: themes?.split(",").map(x => parseInt(x)) ?? [],
        selectedMonths: months?.split(",").map(x => parseInt(x)) ?? [],
        selectedCountries: locations?.split(",").map(x => parseInt(x)) ?? [],
      }, () => this.onFiltersChange());
      return;
    }

    if (this.props.content && this.props.currency.data.id !== newProps.currency.data.id) {
      this.props.refreshContent(this.getCurrentFilterSet(), this.props.content.context.page);
    }
  }

  private renderFilters = () => {

    const panels: FilterPanelDefinition[] = [
      {
        render: (isOpen, onClick) => (
          <FilterPanel title="Destinations"
            svg={InspirationResources.FILTER_LOCATION_ICON}
            isOpen={isOpen}
            onToggle={onClick}
            clearFilters={() => this.onCountryFilterToggle(null)}
            activeCount={this.state.selectedCountries.length}
            maxCount={this.props.filterCountries.data.length}>
            <LocationFilter
              regions={this.props.filterRegions.data}
              countries={this.props.filterCountries.data}
              selectedCountries={this.state.selectedCountries}
              onSelectionToggle={this.onCountryFilterToggle} />
          </FilterPanel>
        ),
      },
      {
        render: (isOpen, onClick) => (
          <FilterPanel title="Months"
            svg={InspirationResources.FILTER_MONTH_ICON}
            isOpen={isOpen}
            onToggle={onClick}
            clearFilters={() => this.onMonthFilterToggle(null)}
            activeCount={this.state.selectedMonths.length}
            maxCount={12}>
            <MonthFilter
              choices={MonthEnumHelper.asArray()}
              selected={this.state.selectedMonths}
              onSelectionToggle={this.onMonthFilterToggle} />
          </FilterPanel>
        ),
      },
      {
        render: (isOpen, onClick) => (
          <FilterPanel title="Themes"
            svg={InspirationResources.FILTER_THEMES_ICON}
            isOpen={isOpen}
            onToggle={onClick}
            clearFilters={() => this.onThemeFilterToggle(null)}
            activeCount={this.state.selectedThemes.length}
            maxCount={this.props.filterThemes.data.length}>
            <ThemeFilter
              choices={this.props.filterThemes ? this.props.filterThemes.data : []}
              selected={this.state.selectedThemes}
              onSelectionToggle={this.onThemeFilterToggle} />
          </FilterPanel>
        ),
      },
      {
        render: (isOpen, onClick) => (
          <FilterPanel title="Types"
            svg={InspirationResources.FILTER_TYPES_ICON}
            isOpen={isOpen}
            onToggle={onClick}
            clearFilters={() => this.onContentTypeToggle(null)}
            activeCount={this.state.selectedTypes.length}
            maxCount={allContentTypes.length}>
            <ContentTypeFilter choices={allContentTypes} selected={this.state.selectedTypes} onSelectionToggle={this.onContentTypeToggle} />
          </FilterPanel>
        ),
      },
    ];

    return <FilterAccordion ref={x => this.filterAccordionRef = x} panels={panels} />;
  }

  private renderHelpMessage = () => {
    // TODO show different text based on user or pre selection
    // let hasSelectedFilters = (this.state.selectedCountries.length + this.state.selectedMonths.length + this.state.selectedThemes.length + this.state.selectedTypes.length) > 0;

    return (
      <p className="help-message">
        Explore our collection of trip ideas, articles and customer reviews to get inspiration for your dream holiday.
      </p>
    );
  }

  private renderContentCards = () => {
    const contentItems = this.props.content;
    if (!contentItems) {
      return null;
    }

    const sortedContent = (contentItems.data || []).sort(Sort.sortDateDesc<IContent>(x => x.publishedTimestamp).compare);

    if (sortedContent.length > 0) {
      const tagContext: IListContentParams = {
        locations: this.state.selectedCountries.map(x => x.id),
        months: this.state.selectedMonths,
        themes: this.state.selectedThemes.map(x => x.id),
      };

      const cards = sortedContent.map(content => (
        <ContentCard
          key={`content-${ContentTypeEnumHelper.toSlug(content.type)}-${content.id}`}
          content={content}
          filterContext={tagContext}
        />
      ));

      const newsletterIndex = Math.min(5, (sortedContent.length / 3) + 5);
      cards.splice(newsletterIndex, 0, <NewsletterSubCard />);

      if (!contentItems.nextIndex) {
        cards.push(
          <FlexiCard
            title="Looking for something else?"
            className="cta-card"
            icomoon={Icon.Icomoon.books}
            description="We're looking to build a comprehensive library for all of our destinations. If there's something you want to know about any of our countries, then tell us - we'd love to know!"
            cta="Get in touch"
            to={WebRoutes.contactUs()}
          />,
        );
      }
      else {
        cards.push(<PlaceholderCard key="placeholder" />);
      }

      return cards.map((card, i) => (
        <div className={this.columnClasses} key={card.key || i}>
          {card}
        </div>
      ));
    }
    else if (contentItems.status === "PENDING") {
      return [
        <div className="content-card no-link loading-card">
          <Loader />
        </div>,
      ];
    }
    else {
      return [
        <FlexiCard title="Sorry! We don't have that"
          className="cta-card"
          onClick={() => this.resetFilters()} // TODO REFACTOR: this shouldn't really be necessary
          icomoon={Icon.Icomoon.reset}
          description="We don't have anything in our collection matching the filters you've chosen. Try using different filters or click here to start again."
          cta="Reset filters"
          to={WebRoutes.inspiration()} />,
      ];
    }
  }

  public render() {
    const meta = [
      {
        name: "description",
        content: "The best holiday destinations, personalised for you. Share your ideas with our local experts for a tailor-made holiday in your dream destination.",
      },
    ];

    const link = [
      { rel: "canonical", href: `${globalConfig.wwwBaseUrl}${WebRoutes.inspiration()}` },
    ];

    const cardSize = this.state.selectedCardSize != null ? this.state.selectedCardSize : CardSize.Mid;

    const cardWrapClassName = classNames("content-cards", { small: cardSize === CardSize.Small }, { mid: cardSize === CardSize.Mid });

    let headerImages = ["07", "08", "25"].map(x => `/images/locations/${x}_small.jpg`);
    if (this.state.selectedCountries && this.state.selectedCountries.length > 0) {
      const recentSelections = _.last(this.state.selectedCountries.filter(x => x.heroImage != null), 3).map(x => x.heroImage.largeUrl);
      headerImages = headerImages.concat(recentSelections);
    }

    return (
      <div className="inspiration">
        <Helmet title="Find travel inspiration with TravelLocal" meta={meta} link={link} />
        <div className="body-wrap">
          <Sticky
            className={classNames("filters-wrap", { "drawer-open": this.state.filterDrawerOpen })}
            top=".site-header"
            bottomBoundary="main"
            activeClass="stuck"
          >
            <div className="filters-full">
              {this.state.filterDrawerOpen &&
                <IconButton className="close-drawer" icomoon={Icon.Icomoon.cross} onClick={this.closeFilterFromCompact} />
              }
              {this.renderFilters()}
            </div>
            <div className="filters-compact">
              <div className="open-filters">
                <div onClick={() => this.openFilterFromCompact(0)}>
                  <SvgIcon svg={InspirationResources.FILTER_LOCATION_ICON} />
                  <span>Place</span>
                </div>
                <div onClick={() => this.openFilterFromCompact(1)}>
                  <SvgIcon svg={InspirationResources.FILTER_MONTH_ICON} />
                  <span>Month</span>
                </div>
                <div onClick={() => this.openFilterFromCompact(2)}>
                  <SvgIcon svg={InspirationResources.FILTER_THEMES_ICON} />
                  <span>Theme</span>
                </div>
                <div onClick={() => this.openFilterFromCompact(3)}>
                  <SvgIcon svg={InspirationResources.FILTER_TYPES_ICON} />
                  <span>Type</span>
                </div>
              </div>
            </div>
          </Sticky>

          <div id="cards-wrap">
            <Measure bounds onResize={this.debounceResize}>
              {({ measureRef }) => (
                <div ref={measureRef} className={cardWrapClassName}>
                  {this.renderContentCards()}
                </div>
              )}
            </Measure>
          </div>
        </div>
      </div>
    );
  }
}

const mapStateToProps = (state: IWebStoreState): StateProps => {
  const content = state.contentByCollection.data[CollectionKeyHelper.Inspiration];

  return {
    content,
    currency: state.currency,
    currentRoute: state.routing,
    filterRegions: state.regions,
    filterCountries: state.countries,
    filterThemes: state.contentThemes,
  };
};

const mapDispatchToProps = (dispatch, ownProps: {}): DispatchProps => {
  return {
    loadContent: async (args: IListContentParams, page: number) => {
      await dispatch(push(WebRoutes.inspiration(args, page))); // update the url first in case the next call fails
      await dispatch(ActionPromises.getContentPage(args, page, CONTENT_PAGE_SIZE, { key: CollectionKeyHelper.Inspiration }));
    },
    refreshContent: (args: IListContentParams, currentPage: number) => {
      const pageSize = Math.abs(currentPage) * CONTENT_PAGE_SIZE;
      return dispatch(ActionPromises.getContentPage(args, -1, pageSize, { key: CollectionKeyHelper.Inspiration }));
    },
    navigate: (route: string) => dispatch(push(route)),
  };
};

export const InspirationIndex = connect(mapStateToProps, mapDispatchToProps)(InspirationIndexComponent);

export const inspirationIndexOnEnter = apiPromiseHook<any>(
  EnterHookPromises.getContentPageFromQuery(CONTENT_PAGE_SIZE),
  EnterHookPromises.contentThemes(18),
  EnterHookPromises.allRegions(),
  EnterHookPromises.allCountries(),
);
